import * as Sentry from "@sentry/react";

import { scrubEvent } from "./sentry-scrub";

/**
 * Initialise Sentry/GlitchTip for the browser.
 *
 * Mirrors the backend gating (vidario-backend/main/settings.py): init only
 * fires when `VITE_GLITCHTIP_DSN_FRONTEND` is set, so dev, tests, and the
 * static MSW demo build stay silent. Browser DSNs are public by design —
 * they ship inside the bundle — so production sets the DSN as a Vite
 * BUILD-ARG in Coolify (a full Redeploy is required; Restart reuses the
 * old bundle).
 *
 * Errors only — no performance tracing, no session replay. GlitchTip is
 * self-hosted on a small instance; we keep volume to actual errors,
 * matching the backend's `traces_sample_rate=0.0`. PII scrubbing lives in
 * `./sentry-scrub::scrubEvent`.
 */
export function initObservability(): void {
  const dsn = import.meta.env.VITE_GLITCHTIP_DSN_FRONTEND;
  if (!dsn) return;

  Sentry.init({
    dsn,
    environment: import.meta.env.VITE_GLITCHTIP_ENV ?? "production",
    // CI bakes VITE_GIT_SHA so events tie back to a release; "unknown"
    // covers a one-off build that has the DSN set but no SHA.
    release: import.meta.env.VITE_GIT_SHA ?? "unknown",
    // Errors only — no APM, no replay (keeps GlitchTip volume + bundle low).
    tracesSampleRate: 0,
    // Never let the SDK attach IP / cookies / default PII; the allow-list
    // scrubber in beforeSend is the second line of defence.
    sendDefaultPii: false,
    // Default integrations still capture window.onerror +
    // unhandledrejection. We deliberately don't add BrowserTracing/Replay.
    beforeSend: scrubEvent,
  });
}

/** The image's `release` is its commit (baked at build time); the product
 * release (issue #34) is only known at runtime from `/health/`, so it rides
 * along as a tag. A no-op when the SDK was never initialised. */
export function tagAppVersion(version: string): void {
  Sentry.setTag("app_version", version);
}

/** Re-exported so call sites import error capture from one place. Safe to
 * call when the SDK was never initialised — it's a no-op then. */
export { captureException } from "@sentry/react";
